import { SketchLogo } from '@phosphor-icons/react/dist/ssr'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { ArrowLeft, CreditCard } from 'react-feather'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { getRequest, postRequest } from 'src/lib/core/api-client'
import { useLocale } from 'src/lib/hooks/useLocal'
import { AppStoreAppType } from 'src/lib/types/AppStoreTypes'
import { apiURI } from 'src/lib/utils/constants'
import dynamic from 'next/dynamic'
import * as productPackage from 'public/images/misc/product-packaging.json'
import { CalendarDots } from '@phosphor-icons/react'
import MediaCollage from './AppDeatailsMediaCollage'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { handleAppInstallAction, refetchShopfloApps } from 'src/lib/utils/AppStoreHelper'
import { currencyFormatter } from 'src/lib/utils/formatters'
import { useAppStorePermissions } from 'src/lib/access-permission-hooks/useAppStorePermissions'
import { classNames } from 'src/lib/utils/helpers'
import useGTM from 'src/lib/hooks/useGTM'

type mediaType = {
  type: 'IMAGE' | 'VIDEO'
  priority: number
  primary: boolean
  url: string
  altText: string
  metadata: any
  height: number
  width: number
}

interface FeatureAppHeroProps {
  appDetails: AppStoreAppType
}

const LottieComponent = dynamic(() => import('lottie-react'), { ssr: false })

const AppDetailsHero: React.FC<FeatureAppHeroProps> = ({ appDetails }) => {
  const router = useRouter()
  const { t } = useLocale()
  const { user, setUserCtx } = useUser()
  const { canInstallApp } = useAppStorePermissions()
  const { sendGTMTrigger } = useGTM()

  const [showInstallationDialogue, setShowInstallationDialogue] = useState(false)
  const [showInstallingGif, setShowInstallingGif] = useState(false)
  const [showCalendy, setShowCalendly] = useState(false)
  const [isloading, setIsLoading] = useState(false)
  const {
    getters: { merchant }
  } = useMerchant()

  const hasTrial = appDetails.merchantAppDetails.trial_period_config.duration > 0

  const {
    actions: { setShopfloApps }
  } = useOrganisationContext()
  const priceFromTiersMapping = Object.values(appDetails.pricingTiers[0]?.feature_price_mapping || {})?.find(
    val => val >= 0
  )
  //This price is needed to check if free or not
  const price = priceFromTiersMapping

  const handleStartTrial = async () => {
    setIsLoading(true)
    const merchantId = merchant?.accountId
    const updateProfile = async () => {
      try {
        const accountData: any = await getRequest('/merchant-user/profile', apiURI.AUTH_ENGINE)
        const data = accountData?.data?.data
        setUserCtx(data)
      } catch (error) {
        console.error('Not able update profile: ', error)
      }
    }

    try {
      const resp = await postRequest(
        `/merchant/${merchantId}/shopflo-apps/${appDetails?.id}/install`,
        apiURI.SETTLEMENT_ENGINE,
        {
          email_id: user?.email,
          tier_id: appDetails.pricingTiers[0].id
        }
      )
      if (!resp?.error) {
        if (handleAppInstallAction(resp)) return
        setIsLoading(false)
        setShowInstallingGif(true)
        setShowInstallationDialogue(false)
        sendGTMTrigger('app_store_app_installed', 'create', {
          appName: appDetails.displayName,
          appKey: appDetails.name
        })
        const shopfloAppsData = await refetchShopfloApps()
        setShopfloApps(shopfloAppsData)
        setTimeout(async () => {
          setShowInstallingGif(false)
          router.push(appDetails?.redirectUrl ?? '/apps-integrations')
          await updateProfile()
        }, 5000)
      }
    } catch (error) {
      console.error('Error: ', error)
    }
  }

  const handleInstallBtnClick = () => {
    const { merchantAppDetails } = appDetails || {}
    const { active, trial_period_status, trial_period_config } = merchantAppDetails || {}

    if (active) {
      const pathname = router.pathname.split('/').slice(0, -1).join('/')
      router.push({
        pathname: `${pathname}/table`
      })
      return
    }

    const shouldShowInstallationDialog = price && trial_period_status === 'NOT_STARTED' && trial_period_config?.enabled

    if (shouldShowInstallationDialog) {
      setShowInstallationDialogue(true)
    } else {
      handleStartTrial()
    }
  }

  // this code is reserved for future use in v-2
  // const BookCallDialogue = () => {
  //   return (
  //     <div className='flex border-1 rounded-lg  py-4 px-4 gap-2 items-start w-[371px] h-[412px] mx-auto'>
  //       <div className='flex flex-col items-center h-[276px]'>
  //         <span className='p-3 rounded-full bg-gray-light'>
  //           <CalendarDots size={16} />
  //         </span>
  //         <hr className='h-20 border-1' />
  //         <span className='p-3 rounded-full bg-gray-light'>
  //           <SketchLogo size={16} />
  //         </span>
  //         <hr className='h-20 border-1' />
  //         <span className='p-3 rounded-full bg-gray-light'>
  //           <CreditCard size={16} />
  //         </span>
  //       </div>
  //       <div className='flex flex-col h-full gap-[65px] pt-2'>
  //         <div className='text-sm font-normal flex flex-col gap-1'>
  //           <h1 className='font-medium text-carbon'>{t('book_a_call_to_setup_feature')}</h1>
  //           <p className='text-gray-dark'>{t('book_call_requires_code_implementation')}</p>
  //         </div>
  //         <div className='text-sm font-normal flex flex-col gap-1'>
  //           <h1 className='font-medium text-carbon'>{t('trial_period_start_after_setup')}</h1>
  //           <p className='text-gray-dark'>{t('try_it_out_for_days')}</p>
  //         </div>
  //         <div className='text-sm font-normal flex flex-col gap-1'>
  //           <h1 className='font-medium text-carbon'>{t('billing_starts_once_trial_ends')}</h1>
  //           <p className='text-gray-dark'>{t('you_will_be_charged_amount')}</p>
  //           <p className='text-gray-dark'>{t('we_will_send_email_before_trial_ends')}</p>
  //         </div>
  //       </div>
  //     </div>
  //   )
  // }

  // const handleOpenCalendly = async () => {
  //   setShowCalendly(true)
  // }

  const InstallationDialogue = () => {
    return (
      <div className='flex border-1 rounded-lg py-4 px-4 gap-2 items-start'>
        <div className='flex flex-col items-center h-36'>
          <span className='p-4 rounded-full bg-gray-light'>
            <SketchLogo size={16} />
          </span>
          <hr className='h-20 border-1' />
          <span className='p-4 rounded-full bg-gray-light'>
            <CreditCard size={16} />
          </span>
        </div>
        <div className='flex flex-col h-full gap-[60px] pt-2'>
          <div className='text-base font-normal flex flex-col gap-1'>
            <h1 className='font-medium text-carbon'>{t('trial_period_starts_today')}</h1>
            <p className='text-gray-dark'>
              {t('start_trial_for_days', { days: appDetails?.merchantAppDetails.trial_period_config?.duration })}
            </p>
          </div>
          <div className='text-base font-normal flex flex-col gap-1'>
            <h1 className='font-medium text-carbon'>{t('billing_starts_once_trial_ends')}</h1>
            <p className='text-gray-dark'>{t('after_trial_billing_info_in_dialogue', { amount: price })}</p>
          </div>
        </div>
      </div>
    )
  }

  const InstallingGif = () => {
    return (
      <div className='flex flex-col gap-4 items-center justify-center'>
        <div className='w-56 h-56'>
          <LottieComponent animationData={productPackage} autoplay={true} />
        </div>
        <p className='text-center'>{t('installing_feature', { feature_name: appDetails.displayName })}</p>
      </div>
    )
  }

  const getInstallBtnText = (status: string) => {
    if (appDetails.type === 'NO_INSTALL') return t('Installed')
    switch (status) {
      case 'ACTIVE':
        return t('manage_app')
      case 'NOT_STARTED':
        return appDetails.merchantAppDetails.trial_period_config.enabled ? t('start_free_trial') : t('install')
      default:
        return appDetails.merchantAppDetails.active ? t('manage_app') : t('install')
    }
  }

  const getInstallButtonBgColor = () => {
    if (!canInstallApp) {
      return 'bg-gray-dark'
    }
    if (appDetails?.merchantAppDetails?.active || appDetails.type === 'NO_INSTALL') {
      return 'bg-carbon'
    }
    return 'bg-primary-dark'
  }

  return (
    <div className='flex gap-14 pb-11'>
      <div className='flex flex-col gap-8 flex-1 min-w-0'>
        <div className='flex flex-col gap-3 items-start'>
          <div className='flex flex-row gap-2 items-center'>
            <button
              className='px-3 py-2 bg-white border rounded-lg max-w-fit border-flo-gray-light'
              onClick={() => router.back()}
            >
              <ArrowLeft size='16' className='font-medium text-coal-dark ' />
            </button>
            <p className='text-text-sm-medium text-gray-500'>All apps</p>
          </div>
          <h1 className='text-xl font-semibold text-carbon'>{appDetails?.displayName}</h1>
        </div>
        <div className='[&_*]:p-0'>
          <p className='p-0' dangerouslySetInnerHTML={{ __html: appDetails?.description ?? '' }}></p>
        </div>

        <button
          disabled={canInstallApp ? isloading || appDetails.type === 'NO_INSTALL' : !canInstallApp}
          className={classNames('text-white py-2 px-3 rounded-xl', getInstallButtonBgColor())}
          onClick={handleInstallBtnClick}
        >
          {getInstallBtnText(appDetails.merchantAppDetails.trial_period_status)}
        </button>

        {price &&
        !appDetails.merchantAppDetails.active &&
        appDetails.merchantAppDetails.trial_period_status !== 'COMPLETED' ? (
          <div className='flex justify-center items-center gap-2 text-sm text-gray-dark pb-4'>
            {hasTrial && appDetails.merchantAppDetails.trial_period_config.enabled && (
              <>
                <p>{t('days_trial_period', { days: appDetails.merchantAppDetails.trial_period_config.duration })}</p>
                <span className='p-1 rounded-full bg-coal-lighter'></span>
              </>
            )}
            {appDetails.merchantAppDetails.trial_period_config.enabled ? (
              <p>{t('amount_per_month_after_trial', { amount: currencyFormatter(price) })}</p>
            ) : (
              <p>{t('amount_per_month', { amount: currencyFormatter(price) })}</p>
            )}
          </div>
        ) : (
          !appDetails.merchantAppDetails.active && (
            <div className='flex justify-center items-center gap-2 text-sm text-gray-dark pb-4 -mt-[15px]'>
              <span className='text-sm text-gray-dark'>
                {price ? t('amount_per_month', { amount: currencyFormatter(price) }) : t('free')}
              </span>
              {appDetails.merchantAppDetails.marked_up_price && (
                <span className='text-xs line-through'>
                  {currencyFormatter(appDetails.merchantAppDetails.marked_up_price)}
                </span>
              )}
            </div>
          )
        )}
      </div>
      <div className='w-[580px] flex-shrink-0 flex flex-col items-center justify-center'>
        <MediaCollage medias={appDetails.media} />
      </div>

      {/* <FloGenericDialog
        title={appDetails?.name}
        isOpen={showInstallationDialogue}
        handleClose={setShowInstallationDialogue}
        content={<BookCallDialogue />}
        jobButtonText='Book call'
        closeButtonText='Cancel'
        handleJob={handleOpenCalendly}
      /> */}

      <FloGenericDialog
        title=''
        isOpen={showCalendy}
        handleClose={setShowCalendly}
        content={<iframe src='https://calendly.com/d/cntr-syq-kfc/shopflo-cart-setup-call' height={600} width='100%' />}
      />

      <FloGenericDialog
        title={appDetails?.displayName}
        isOpen={showInstallationDialogue}
        handleClose={setShowInstallationDialogue}
        content={<InstallationDialogue />}
        jobButtonText={'Start trial'}
        closeButtonText={'Cancel'}
        handleJob={handleStartTrial}
        jobButtonDisabled={isloading}
        hideTopBottomBorder={true}
      />
      <FloGenericDialog
        title={''}
        isOpen={showInstallingGif}
        handleClose={setShowInstallingGif}
        content={<InstallingGif />}
        jobButtonText={''}
        closeButtonText={''}
        handleJob={undefined}
        hideCloseIcon={showInstallingGif}
        hideTitleBar={true}
        primaryBtnEnabled={false}
        hideTopBottomBorder={true}
        shouldDisableOutsideClick={true}
      />
    </div>
  )
}

export default AppDetailsHero
